import React, { useEffect, useState } from "react";
import { Box, Flex } from "theme-ui";
import moment from "moment";
import BasicButton from "components/forms/BasicButton";
import { updateProject } from "api/Projects";
import { useToast } from "components/toast";
import { themevals } from "theme/themevals";

/**
 * CLIENT'S "DATE NEEDED BY" (2026-10-12, Jack). Theirs, not our service
 * date: their saved date, else the RFQ date while it's still ahead, else
 * blank. Saves project.clientNeededBy; scheduling and the rush flag never
 * read it. One field, used in the quote-accepted window, the project's
 * Next steps card and the project sidebar.
 */
type Props = {
  projectId?: string | null;
  neededBy?: string | null;
  rfqDate?: string | Date | null;
  /** Shown before the input; omit when the caller has its own label. */
  label?: string;
  onSaved?: (value: string) => void;
};

const NeededByField: React.FC<Props> = ({ projectId, neededBy, rfqDate, label, onSaved }) => {
  const toast = useToast();
  const today = moment().format("YYYY-MM-DD");
  const saved0 = neededBy ? String(neededBy).slice(0, 10) : "";
  const rfqDay = rfqDate ? moment.utc(rfqDate).format("YYYY-MM-DD") : "";
  const [value, setValue] = useState(saved0 || (rfqDay && rfqDay >= today ? rfqDay : ""));
  const [saved, setSaved] = useState(saved0);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setSaved(saved0);
    if (saved0) setValue(saved0);
  }, [saved0]);

  const save = async () => {
    if (!projectId) return;
    setBusy(true);
    const res: any = await updateProject({ data: { id: projectId }, update: { clientNeededBy: value } });
    setBusy(false);
    if (res?.id) {
      setSaved(value);
      onSaved?.(value);
    } else {
      toast.add(res?.message || "Could not save the date. Try again.", "var(--theme-ui-colors-danger)");
    }
  };

  return (
    <Flex sx={{ alignItems: "center", gap: "8px", flexWrap: "wrap", fontSize: "13px", fontFamily: "Poppins, sans-serif" }}>
      {label && <Box sx={{ color: "#111", fontWeight: 500 }}>{label}</Box>}
      <input
        type="date"
        aria-label="Date needed by"
        value={value}
        min={today}
        onChange={(e) => setValue(e.target.value)}
        style={{ fontFamily: "Poppins, sans-serif", fontSize: "13px", padding: "6px 8px", border: "1px solid " + themevals.colors.inputOutline, borderRadius: "6px" }}
      />
      {value !== saved && (value || saved) ? (
        <BasicButton type="button" scheme="secondary" outline={true} disabled={busy} $submitting={busy} keepText={true} $loadingColor="#000" onClick={() => void save()}>
          Save
        </BasicButton>
      ) : saved ? (
        <Box sx={{ fontSize: "12px", color: "#27500A" }}>Saved</Box>
      ) : null}
    </Flex>
  );
};

export default NeededByField;
